<template>
  <div class="ag-table-wrapper bg-white-1 shadow3 q-pt-md">
    <AgGrid
      v-if="financialPeriodId"
      ref="agGrid"
      :options="options"
      :api-request="infiniteScrollRequest"
      class="q-px-lg q-pb-lg"
      @row-selected="selectionChanged"
      @filter-changed="filterChanged"
      @cell-value-changed="cellValueChanged"
      @new-rows-loaded="newRowsLoaded"
      @ready="onGridReady"
    >
      <template #header>
        <Grid justify-content="end">
          <ShowHideCols :ag-grid="agGrid" />
        </Grid>
      </template>
    </AgGrid>
  </div>
</template>

<script>
import * as apiTo from '@/modular-api'
import agGridPendingStatusMixin from '@/modules/financial/subpages/predictions-tab/shared/ag-grid-pending-status-mixin'
// Mixins
import gridOptions from './grid-options'
// Libraries
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import { cloneDeep, debounce } from 'lodash'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import ShowHideCols from '@/ag-grid/components/show-hide-cols/ShowHideCols'

export default {
  components: {
    AgGrid,
    ShowHideCols
  },

  mixins: [agGridPendingStatusMixin, gridOptions],

  data: () => ({
    agGrid: null,
    justFiltered: false
  }),

  computed: {
    ...mapState('predictions', ['financialPeriodId']),
    ...mapState('price-forecast', ['rowsSelected']),
    ...mapGetters('entity-labels', ['departmentLabel']),

    totalRows: {
      get() {
        return this.$store.state['price-forecast'].totalRows
      },

      set(newVal) {
        this.setTotalRows(newVal)
      }
    },

    rowsSelected: {
      get() {
        return this.$store.state['price-forecast'].rowsSelected
      },

      set(newVal) {
        this.setRowsSelected(newVal)
      }
    },

    selectAll: {
      get() {
        return this.$store.state['price-forecast'].selectAll
      },
      set(newVal) {
        this.setSelectAll(newVal)
      }
    },

    filterInProgress: {
      get() {
        return this.$store.state['price-forecast'].filterInProgress
      },
      set(newVal) {
        this.setFilterInProgress(newVal)
      }
    }
  },

  watch: {
    financialPeriodId() {
      this.setRowsSelected([])
      this.setSelectAll(false)
      this.$emit('rerender-table')
    },

    selectAll(newVal) {
      if (this.justFiltered) return
      this.rowsSelected = []
      this.$refs.agGrid.selectOrDeselectAll(newVal)
    },

    rowsSelected(newVal) {
      if (!this.totalRows) return

      if (newVal.length === this.totalRows && this.selectAll) {
        // if selectAll checkbox is checked and all rows are unchecked
        // clear selectAll and selected rows
        this.rowsSelected = []
        this.selectAll = false
      } else if (newVal.length === this.totalRows && !this.selectAll) {
        // if all rows are selected check the selectAll checkbox
        this.selectAll = true
      }
    }
  },

  methods: {
    ...mapMutations('price-forecast', ['setTotalRows', 'setRowsSelected', 'setSelectAll', 'setFilterInProgress']),
    ...mapActions('price-forecast', ['getAndSetOverviewData']),

    infiniteScrollRequest(params) {
      return apiTo.GET_PAGINATED_SUBSCRIPTIONS_PERIODS_FOR_FINANCIAL_PERIOD(this.financialPeriodId, params)
    },

    didValueChanged(old, newVal) {
      if (typeof old === 'string' && typeof newVal === 'number') return Number(old) !== Number(newVal)
      return old !== newVal
    },

    cellValueChanged(event) {
      if (!this.didValueChanged(event.oldValue, event.newValue)) return // no change happened
      apiTo
        .MODIFY_SUBSCRIPTION_PERIOD_FOR_FINANCIAL_PERIOD(event.data.id, { [event.column.colId]: event.newValue })
        .then(({ data }) => {
          this.$refs.agGrid.updateRow(event.data.id, data.result)
          this.getAndSetOverviewData()
        })
        .catch(() => {
          this.$refs.agGrid.updateRow(event.data.id, {
            ...event.data,
            [event.column.colId]: event.oldValue
          })
        })
    },

    selectionChanged: debounce(function({ row, isSelected }) {
      const clonedRow = cloneDeep(row)

      if (this.selectAll) {
        if (!isSelected) this.rowsSelected = [...this.rowsSelected, clonedRow]
        else this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
      } else {
        if (!isSelected) this.rowsSelected = this.rowsSelected.filter(selectedRow => selectedRow.id !== row.id)
        else this.rowsSelected = [...this.rowsSelected, clonedRow]
      }
    }, 0),

    filterChanged() {
      this.filterInProgress = true
      this.justFiltered = true
      this.selectAll = false
      this.rowsSelected = []
    },

    newRowsLoaded({ totalRows, newRows }) {
      this.filterInProgress = false
      const newRowsIds = newRows.map(row => row.id)

      this.totalRows = totalRows
      if (this.justFiltered) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(false)
        })
        this.justFiltered = false
      } else if (this.selectAll) {
        this.agGrid.api.forEachNode(node => {
          if (newRowsIds.includes(node.id)) node.setSelected(true)
        })
      }
    },

    purgeServerSideCache() {
      this.filterChanged()
      this.$refs.agGrid.purgeServerSideCache()
    }
  }
}
</script>

<style lang="scss" scoped>
.ag-table-wrapper {
  grid-area: table;
  height: 500px;
}
</style>
